import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Attribute, AttributeValue } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { MediaIdField } from '@/features/media';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyAttributeValueForm,
  fromAttributeValue,
  toAttributeValueCreate,
  toAttributeValueUpdate,
  valueCodeFrom,
} from '../api/attribute-mappers';
import { normalizeSkuCode, skuCodeFrom } from '../api/sku-rule-mappers';
import { useCreateAttributeValue, useUpdateAttributeValue } from '../hooks/use-attribute-mutations';
import {
  attributeValueFormSchema,
  type AttributeValueFormInput,
  type AttributeValueFormValues,
} from '../schemas/attribute-form-schema';

export interface AttributeValueDialogProps {
  attribute: Attribute;
  /** The value being edited; null adds a new one at the end. */
  value: AttributeValue | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function DialogBody({
  attribute,
  value,
  onClose,
}: {
  attribute: Attribute;
  value: AttributeValue | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['attributes', 'common']);
  const message = useErrorMessage();
  const create = useCreateAttributeValue({
    successMessage: 'attributes:toasts.valueCreated',
    silentError: true,
  });
  const update = useUpdateAttributeValue();
  const isPending = create.isPending || update.isPending;
  const form = useForm<AttributeValueFormInput, unknown, AttributeValueFormValues>({
    resolver: useI18nResolver(attributeValueFormSchema),
    defaultValues: value ? fromAttributeValue(value) : emptyAttributeValueForm(),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  // The code and the SKU code follow the label until each is edited by hand.
  const codeTouched = useRef(false);
  const skuCodeTouched = useRef(false);
  const swatch = form.watch('swatch');

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (value) {
        const updated = await update.mutateAsync({
          attributeId: attribute.id,
          valueId: value.id,
          body: toAttributeValueUpdate(values),
        });
        // A changed code never rewrites SKUs; say how many still carry the old one.
        if (updated.affectedVariantCount > 0) {
          toast.message(t('attributes:skuCode.affected', { count: updated.affectedVariantCount }));
        }
      } else {
        await create.mutateAsync({
          attributeId: attribute.id,
          body: toAttributeValueCreate(values),
        });
      }
      onClose();
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert" className="sm:col-span-2">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('attributes:valueFields.label')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!value && !codeTouched.current)
                      form.setValue('value', valueCodeFrom(e.target.value));
                    if (!value && !skuCodeTouched.current)
                      form.setValue('skuCode', skuCodeFrom(e.target.value));
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('attributes:valueFields.code')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={value !== null}
                  className="font-mono text-[13px]"
                  onChange={(e) => {
                    codeTouched.current = true;
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormDescription>
                {value
                  ? t('attributes:valueFields.codeFixed')
                  : t('attributes:valueFields.codeHint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="skuCode"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('attributes:valueFields.skuCode')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  maxLength={12}
                  autoCapitalize="characters"
                  spellCheck={false}
                  className="max-w-40 font-mono text-[13px] uppercase"
                  onChange={(e) => {
                    skuCodeTouched.current = true;
                    field.onChange(normalizeSkuCode(e.target.value));
                  }}
                />
              </FormControl>
              <FormDescription>{t('attributes:valueFields.skuCodeHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="swatch"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('attributes:valueFields.swatch')}</FormLabel>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  aria-label={t('attributes:valueFields.swatchPicker')}
                  className="h-9 w-12 shrink-0 cursor-pointer p-1"
                  value={HEX.test(swatch) ? swatch : '#000000'}
                  onChange={(e) =>
                    form.setValue('swatch', e.target.value.toUpperCase(), {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                />
                <FormControl>
                  <Input
                    {...field}
                    placeholder="#F3D27A"
                    maxLength={7}
                    className="max-w-40 font-mono text-[13px]"
                  />
                </FormControl>
              </div>
              <FormDescription>{t('attributes:valueFields.swatchHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="swatchMediaId"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('attributes:valueFields.swatchImage')}</FormLabel>
              <FormControl>
                <MediaIdField
                  value={field.value}
                  onChange={field.onChange}
                  label={t('attributes:valueFields.swatchImage')}
                />
              </FormControl>
              <FormDescription>{t('attributes:valueFields.swatchImageHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter className="sm:col-span-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending
              ? t('common:actions.saving')
              : value
                ? t('common:actions.save')
                : t('attributes:valueDialog.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Add a value to a select or multiselect attribute, or rename one and change its swatch. */
export function AttributeValueDialog({
  attribute,
  value,
  open,
  onOpenChange,
}: AttributeValueDialogProps) {
  const { t } = useTranslation('attributes');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {value
              ? t('valueDialog.editTitle', { label: value.label })
              : t('valueDialog.createTitle', { attribute: attribute.label })}
          </DialogTitle>
          <DialogDescription>
            {value ? t('valueDialog.editDescription') : t('valueDialog.createDescription')}
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <DialogBody
            key={value?.updatedAt ?? 'new'}
            attribute={attribute}
            value={value}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
